import { Button, Container, Flex, Heading, Image, Text, useBreakpointValue } from '@chakra-ui/react';
import { useResources } from 'datx-jsonapi-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProfilePopupNew } from '@/components/ui/shared/profile/ProfilePopup/ProfilePopupNew';
import { SearchGroup } from '@/enums';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryThinProfilesForIndexSearch } from '@/queries/thin-profiles';
import { ThinProfile } from '@/resources/ThinProfile';

interface ILectureURLSearchParams {
  query?: string;
  eventId?: string;
  searchGroup?: SearchGroup;
}

const fallbackImageUrl = '/images/landing/conferences-gradient.png';

export const ProfileSearchSection = () => {
  const { t } = useTranslation();
  const isLarge = useBreakpointValue({ base: false, '2xl': true });
  const { query, eventId, change } = useQueryParams<ILectureURLSearchParams>({
    query: StringParam,
    eventId: StringParam,
    searchGroup: StringParam,
  });
  const [popupProfile, setPopupProfile] = useState<null | ThinProfile>(null);

  const filters = {
    event_id: eventId,
    query: query || '',
  };

  const { data = [], meta } = useResources(queryThinProfilesForIndexSearch(filters, isLarge ? 5 : 4, 1));

  function handleShowAllClick() {
    change({ query, eventId, searchGroup: SearchGroup.SPEAKERS }, { shallow: true, method: 'push' });
  }

  const noResults = meta?.totalCount === 0;

  return (
    <Flex flexDir="column" p={6} borderColor="gray.200" borderBottomWidth="1px" pb={6}>
      <Flex w="full" justifyContent="space-between" mb={4}>
        <Heading size="sm">Speakers/Authors</Heading>
        {!noResults && (
          <Button variant="link" onClick={handleShowAllClick} color="gray.800" size="md">
            {t('schedule.search.showAllNumber', { totalCount: meta?.totalCount || 0 })}
          </Button>
        )}
      </Flex>

      <Container px={0} maxW="container.2xl">
        {noResults && <Text>{t('schedule.search.noSpeakers')}</Text>}
        <Flex alignItems="flex-start" justifyContent={{ base: 'center', lg: 'flex-start' }} flexWrap="wrap" gap={6}>
          {data?.length > 0 &&
            data.map((profile) => (
              <Flex
                w="288px"
                onClick={() => setPopupProfile(profile)}
                key={profile.id}
                textAlign="center"
                alignItems="center"
                p="4"
                flexDir="column"
                cursor="pointer"
                boxSizing="border-box"
                _hover={{
                  boxShadow: 'card2xl',
                }}
              >
                <Image
                  borderRadius="full"
                  width="200px"
                  height="200px"
                  src={profile.mediumSquareAvatarUrl || fallbackImageUrl}
                  alt={profile.firstName}
                  objectFit="cover"
                />
                <Heading fontSize="3xl" mt="6">
                  {`${profile.firstName} ${profile.lastName}`}
                </Heading>
                <Text fontSize="sm" color="gray.800" mt="2">
                  {profile.title}
                </Text>
              </Flex>
            ))}
        </Flex>
      </Container>
      <ProfilePopupNew profiles={[popupProfile]} isOpen={Boolean(popupProfile)} onClose={() => setPopupProfile(null)} />
    </Flex>
  );
};
